import type { IFrontendServiceContainer } from '@phi/frontend/service-container';
import 'toastr/toastr.scss';
import './toastr.scss';
import * as toastr from 'toastr';
import { periodic_log_factory } from './periodic-log';
import { wrapped_parameter } from '@phi/wrapped-value';
import { ENV } from '@phi/config/frontend';
import { logging_api_factory } from './api';

export type ILogging = ReturnType<typeof logging_factory>;

export const logging_factory = (app: IFrontendServiceContainer) => {
    const api = logging_api_factory(app);
    return {
        periodic_log: periodic_log_factory(app.state, app.authentication.session_type, api.log),
        display_message_to_user,
        LOG,
        ...api,
    };
};

export enum LOG {
    FATAL = 0,
    ERROR = 1,
    WARN = 2,
    INFO = 3,
    DEBUG = 4,
    OTHER = 100,
}

export const uid_messages_suppressed = wrapped_parameter(false);

export function display_message_to_user(
    level: LOG,
    msg: string,
    preventDuplicates = true,
    onclick: (() => void) = () => true,
) {
    const log_level_texts: Record<LOG, string> = {
        [LOG.FATAL]: 'FATAL ERROR',
        [LOG.ERROR]: 'ERROR',
        [LOG.WARN]: 'WARNING',
        [LOG.INFO]: 'INFO',
        [LOG.DEBUG]: 'DEBUG MSG',
        [LOG.OTHER]: 'OTHER MSG',
    };

    let uid_message_title;
    if (level == LOG.INFO)
        uid_message_title = 'Please note:';
    else
        uid_message_title = 'Level = ' + log_level_texts[level];

    const uid_message_body = msg;

    console.log(msg);

    if (!uid_messages_suppressed()) {
        const options = level < LOG.WARN
            ? {
                closeButton: true,
                debug: false,
                progressBar: true,
                preventDuplicates: preventDuplicates,
                positionClass: 'toast-bottom-right',
                onclick,
                showDuration: 1000,
                hideDuration: 1000,
                timeOut: 5000,
                extendedTimeOut: 6000,
                showEasing: 'swing',
                hideEasing: 'linear',
                showMethod: 'fadeIn',
                hideMethod: 'fadeOut'
            }
            : {
                closeButton: true,
                debug: false,
                progressBar: true,
                preventDuplicates: preventDuplicates,
                positionClass: 'toast-bottom-right',
                onclick,
                showDuration: 1000,
                hideDuration: 1000,
                timeOut: 5000,
                extendedTimeOut: 6000,
                showEasing: 'swing',
                hideEasing: 'linear',
                showMethod: 'fadeIn',
                hideMethod: 'fadeOut'
            };

        switch (level) {
            case LOG.FATAL:
            case LOG.ERROR:
                return toastr.warning(uid_message_body, 'Error:', options);

            case LOG.WARN:
                return toastr.warning(uid_message_body, 'Warning:', options);

            case LOG.DEBUG:
                if (ENV === 'development')
                    return toastr.success(uid_message_body, 'Debug:', options);
                else
                    return;

            default:
                return toastr.success(uid_message_body, 'Note:', options);
        }
    }
}

export function clear_all_messages(no_animation = false) {
    if (no_animation)
        toastr.remove();
    else
        toastr.clear();
}

let withSuppressedLoggingSemaphore = false;
/** execute any function with suppressed logging */
export function withSuppressedLogging<IN extends Array<any>, OUT>(f: (...xs: IN) => OUT) {
    return function withSuppressedLogging(...xs: IN): OUT {
        if (withSuppressedLoggingSemaphore){
            return f(...xs);
        } else {
            withSuppressedLoggingSemaphore = true;
            uid_messages_suppressed(true);
            const result = f(...xs);
            uid_messages_suppressed(false);
            withSuppressedLoggingSemaphore = false;
            return result;
        }
    };
}
